import { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import { Navbar } from './components/Navbar';
import { LeftSidebar } from './components/LeftSidebar';
import { Canvas, ExportFrames } from './components/Canvas';
import { Filmstrip } from './components/Filmstrip';
import { Inspector } from './components/Inspector';
import { MagicDraftModal } from './components/MagicDraftModal';
import { PresentMode } from './components/PresentMode';
import { ForcedPasswordScreen, LoginScreen } from './components/auth/AuthScreens';
import { useDeckStore } from './store/deckStore';
import { useAuthStore } from './store/authStore';
import { useShortcuts } from './hooks/useShortcuts';
import { HomePage } from './components/home/HomePage';
import { AnnouncementsPage } from './components/home/AnnouncementsPage';
import { AssetsPage } from './components/home/AssetsPage';
import { DocumentsPage } from './components/home/DocumentsPage';
import { SearchPage } from './components/home/SearchPage';
import { DepartmentPage } from './components/home/DepartmentPage';
import { MyDecksPage } from './components/home/MyDecksPage';
import { PortalLayout } from './components/home/PortalLayout';
import { DeckViewer } from './components/viewer/DeckViewer';
import { navigate, paths, useRoute } from './lib/router';

function Editor({ initialMagicOpen = false }: { initialMagicOpen?: boolean }) {
  const [magicOpen, setMagicOpen] = useState(initialMagicOpen);
  useShortcuts();

  return (
    <Box sx={{ display: 'flex', height: '100vh', flexDirection: 'column', overflow: 'hidden' }}>
      <Navbar onMagicDraft={() => setMagicOpen(true)} />
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <LeftSidebar />
        <Box component="main" sx={{ display: 'flex', flex: 1, minWidth: 0, flexDirection: 'column' }}>
          <Canvas />
          <Filmstrip />
        </Box>
        <Inspector />
      </Box>
      <MagicDraftModal open={magicOpen} onClose={() => setMagicOpen(false)} />
      <PresentMode />
      <ExportFrames />
    </Box>
  );
}

/** Offline (no API): edit the locally cached deck, as before accounts existed. */
function OfflineEditor() {
  const loadOrCreate = useDeckStore((s) => s.loadOrCreate);
  useEffect(() => {
    void loadOrCreate();
  }, [loadOrCreate]);
  return <Editor />;
}

function FullPageSpinner({ label }: { label: string }) {
  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
      <CircularProgress size={28} aria-label={label} />
    </Box>
  );
}

/** Opens deck `id` for editing. Decks you can't edit (shared by others) go to the viewer instead. */
function EditorRoute({ id, openAi }: { id: string; openAi: boolean }) {
  const openDeck = useDeckStore((s) => s.openDeck);
  const loadedId = useDeckStore((s) => s.deckId);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (useDeckStore.getState().deckId === id) {
      if (!useDeckStore.getState().canEdit) navigate(paths.view(id), { replace: true });
      return;
    }
    let cancelled = false;
    openDeck(id)
      .then(() => {
        if (!cancelled && !useDeckStore.getState().canEdit) navigate(paths.view(id), { replace: true });
      })
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : 'Could not open this deck'));
    return () => {
      cancelled = true;
    };
  }, [id, openDeck]);

  useEffect(() => {
    // Drop ?ai=1 once the editor has mounted with the dialog open, so a reload doesn't reopen it.
    if (openAi && loadedId === id) navigate(paths.edit(id), { replace: true });
  }, [openAi, loadedId, id]);

  if (error) {
    return (
      <Box sx={{ maxWidth: 520, mx: 'auto', mt: 10, px: 2, textAlign: 'center' }}>
        <Alert severity="warning" sx={{ textAlign: 'left', mb: 2 }}>
          {error === 'Deck not found' ? "This deck doesn't exist or isn't yours to edit." : error}
        </Alert>
        <Button variant="contained" onClick={() => navigate(paths.home())}>
          Back to home
        </Button>
      </Box>
    );
  }
  if (loadedId !== id) return <FullPageSpinner label="Opening deck" />;
  return <Editor key={id} initialMagicOpen={openAi} />;
}

export default function App() {
  const status = useAuthStore((s) => s.status);
  const mustChangePassword = useAuthStore((s) => s.user?.mustChangePassword ?? false);
  const init = useAuthStore((s) => s.init);
  const route = useRoute();

  useEffect(() => {
    void init();
  }, [init]);

  if (status === 'loading') return <FullPageSpinner label="Loading" />;
  if (status === 'signedOut') return <LoginScreen />;
  if (status === 'offline') return <OfflineEditor />;
  if (mustChangePassword) return <ForcedPasswordScreen />;
  if (route.name === 'edit') return <EditorRoute id={route.id} openAi={route.query.get('ai') === '1'} />;
  if (route.name === 'view') return <DeckViewer key={route.id} id={route.id} />;
  return (
    <PortalLayout route={route}>
      {route.name === 'announcements' ? (
        <AnnouncementsPage />
      ) : route.name === 'assets' ? (
        <AssetsPage />
      ) : route.name === 'documents' ? (
        <DocumentsPage />
      ) : route.name === 'search' ? (
        <SearchPage key={route.q} q={route.q} />
      ) : route.name === 'mine' ? (
        <MyDecksPage />
      ) : route.name === 'dept' ? (
        <DepartmentPage key={route.id} id={route.id} />
      ) : (
        <HomePage />
      )}
    </PortalLayout>
  );
}
